import type { AccountLink } from '@t4s/api-client/model'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { messageFor } from '../lib/messages.ts'
import { Notice } from '../shell/notice.tsx'
import { CopyButton } from './copy-button.tsx'
import { formatInstantWithoutZone } from './instants.ts'
import { TenantAddress } from './tenant-address.tsx'

/**
 * The hand-off — docs/ux/PLATFORM-ADMIN.md §10, and the single most security-relevant
 * component in this application.
 *
 * **What it holds is not a password.** The platform owner never sees, sets or knows a
 * tenant user's password. Creating a tenant mints its first superadmin an *activation
 * link*: the same single-use, short-lived, revocable object every invite in this system
 * is, and the person it belongs to opens it and chooses their own password (§10.1).
 *
 * **It is shown once.** The server stores only a hash, so "show it again" is not a feature
 * that was cut — it does not exist, for anyone, including whoever holds the database. The
 * panel says that *above* the value rather than after it (§10.2).
 *
 * **It is a panel, not a dialog** (§5.3), deliberately: a dialog can be dismissed by
 * accident, and this one holds a value that cannot be shown twice. No scrim, no Escape, no
 * close cross. The only way out that is not navigation is the explicit button.
 *
 * Every rule in §10.4 is implemented here and checkable from outside:
 *
 * | Risk | What stops it |
 * |---|---|
 * | Left on screen | Masked by default; re-masks after 60 s revealed, on the tab losing focus, when the page is hidden (a phone switching apps) and when the print dialog opens |
 * | In a screenshot or a printout | The same masking, plus a print rule that hides the value region entirely |
 * | In the URL | The value is never a route or query parameter. Nothing about it is navigable |
 * | In the query cache | It arrives from a **mutation**, is read once into this component's state, and is written to no store |
 * | In a log | It is never passed to `console` in any branch, including error branches |
 * | In a password manager | It is rendered as text, never as an `<input>` inside a `<form>` |
 * | Read out by a screen reader while masked | The masked state is `aria-hidden` and Reveal is a real toggle with `aria-pressed` |
 */

/** How long a revealed value stays revealed before it re-masks itself (§10.3). */
export const REVEAL_TIMEOUT_MS = 60_000

export type HandoffPanelProps = {
  /** What the person will sign in with. Not a secret; readable from the tenant page. */
  readonly nickname: string
  readonly fullName: string
  readonly link: AccountLink
  /**
   * The tenant's slug, for the row naming the hostname this link is redeemed at — this
   * estate's, and both candidates when the estate is unknown (the same `TenantAddress` every
   * other screen uses, so the rule is stated once).
   */
  readonly slug: string
  /** "I have delivered this" — clears the value and returns to the tenant's page. */
  readonly onDone: () => void
}

export function HandoffPanel({ nickname, fullName, link, slug, onDone }: HandoffPanelProps) {
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copyFailure, setCopyFailure] = useState<string | undefined>(undefined)
  const valueId = useId()
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const mask = useCallback(() => {
    setRevealed(false)
  }, [])

  // Re-masks after sixty seconds revealed. Re-masking never destroys the value; Reveal
  // shows it again for as long as the panel lives (§10.3).
  useEffect(() => {
    if (!revealed) {
      return undefined
    }
    timer.current = setTimeout(mask, REVEAL_TIMEOUT_MS)
    return () => {
      clearTimeout(timer.current)
    }
  }, [revealed, mask])

  // On the tab losing focus, on the page being hidden, and when the print dialog opens. The
  // print listener is the belt to the print stylesheet's braces: the rule hides the region, and
  // this makes the value stop existing in the DOM before the page is rasterised.
  //
  // **The page being hidden is what a phone does instead of blurring** (P08): switching to
  // another app, locking the screen or opening the tab switcher fires `visibilitychange` (and
  // `pagehide`) and does not reliably fire `blur` on the window, so a link revealed on a phone
  // would otherwise still be on screen when the owner came back to it.
  useEffect(() => {
    const onBlur = () => {
      mask()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        mask()
      }
    }
    window.addEventListener('blur', onBlur)
    window.addEventListener('beforeprint', onBlur)
    window.addEventListener('pagehide', onBlur)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('beforeprint', onBlur)
      window.removeEventListener('pagehide', onBlur)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [mask])

  const copy = async () => {
    setCopyFailure(undefined)
    try {
      // Confirms **only after the clipboard write resolves** (§10.3). The old system said
      // "copied" whether or not it was.
      await navigator.clipboard.writeText(link.url)
      setCopied(true)
    } catch {
      // The thrown value is deliberately not inspected and never logged: it is a
      // permission failure whose text is the browser's, and this application renders its
      // own sentence by code (§20.1, §20.3).
      setCopied(false)
      setCopyFailure('client_clipboard_denied')
    }
  }

  return (
    <section className="handoff" aria-labelledby={`${valueId}-heading`}>
      <h2 id={`${valueId}-heading`}>Hand this over now</h2>

      <p>
        <strong>This link is shown once.</strong> The server keeps only a hash of it, so nothing and
        nobody can show it again — not this console, not the API, not whoever holds the database. If
        it is lost, mint a new one from the tenant’s page; that revokes this one and costs nothing.
      </p>

      <dl className="kv handoff-facts">
        <dt>For</dt>
        <dd className="row row-tight">
          <span>
            {fullName} — signs in as <code>{nickname}</code>
          </span>
          {/* Not a secret (§10.3): plain text, copyable, never masked. */}
          <CopyButton value={nickname} noun="nickname" quiet />
        </dd>
        <dt>Works until</dt>
        <dd>
          {formatInstantWithoutZone(link.expiresAt, link.timeZone)} <code>{link.timeZone}</code> —
          and it works <strong>once</strong>.
        </dd>
        <dt>Redeemed at</dt>
        <dd>
          <TenantAddress slug={slug} tense="bare" />
        </dd>
      </dl>

      {/*
        `data-revealed` is what the stylesheet draws the two states from. It changes no
        behaviour: `revealed` is the same flag the value below is rendered from, so the
        marker cannot disagree with what is on screen. The word beside it exists because
        "is the secret currently visible" must not be answerable only by colour or only by
        looking at the characters (§21) — and because "On screen" is the state worth
        naming out loud.
      */}
      <div className="handoff-value" data-revealed={revealed} data-testid="handoff-value">
        <div className="handoff-value-head">
          <span className="field-label" id={`${valueId}-label`}>
            {link.purpose === 'password_reset' ? 'Password-reset link' : 'Activation link'}
          </span>
          <span className="handoff-status">{revealed ? 'On screen' : 'Hidden'}</span>
        </div>
        {revealed ? (
          // Selectable text that wraps and is never truncated. Not an `<input>`, and not
          // inside a `<form>`, so nothing offers to save it to a password manager.
          <output className="handoff-secret" htmlFor={`${valueId}-label`} id={valueId}>
            {link.url}
          </output>
        ) : (
          <p className="handoff-secret handoff-masked" aria-hidden="true">
            ••••••••••••••••••••••••••••••••
          </p>
        )}

        <div className="handoff-controls">
          <button
            type="button"
            className="button"
            aria-pressed={revealed}
            aria-controls={valueId}
            onClick={() => {
              setRevealed(!revealed)
            }}
          >
            {revealed ? 'Hide' : 'Reveal'}
          </button>
          <button type="button" className="button" onClick={copy}>
            Copy link
          </button>
          {/* Copying without revealing is the safest path, so it is the easy one. */}
          <span className="muted">Copying works whether or not it is revealed.</span>
        </div>
      </div>

      {copied ? (
        <Notice tone="success" assertive tag="Copied">
          Copied to the clipboard.
        </Notice>
      ) : null}
      {copyFailure === undefined ? null : (
        <Notice tone="failure">{messageFor('handoff', copyFailure)}</Notice>
      )}

      <p className="muted">
        Navigating away loses this value, and so does closing the tab or the session ending. Nothing
        is stored.
      </p>

      <button type="button" className="button button-primary" onClick={onDone}>
        I have delivered this
      </button>
    </section>
  )
}
